import _ from "lodash";
import React, {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Cell, Pie, PieChart, ResponsiveContainer} from "recharts";
import {
    Box,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import SquareRoundedIcon from "@mui/icons-material/SquareRounded";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {InsightOverviewData} from "../../types/domain/insightOverviewData";
import {CROPS} from "../../utils/enumItemsUtils";
import {HelpPopover} from "../base/HelpPopover";

export interface Props {
    overviewData: InsightOverviewData | undefined;
    isLoading: boolean;
}

export const InsightsOverviewCard: React.FC<Props> = ({overviewData, isLoading}) => {
    const {t} = useTranslation();
    const data = useMemo(() => {
        const chartDataProps = ((overviewData?.crops as OverviewChartDataProps[]) ?? []).map((c) => ({
            cropId: c.cropId,
            cropName: t(`enumerations.crops.${CROPS.find((crop) => crop.id === c.cropId)?.i18nKey}`) ?? c.cropName,
            areaInHa: c.areaInHa,
            fieldsNumber: c.fieldsNumber,
        }));

        const newData: OverviewChartDataProps[] = _.orderBy(chartDataProps, "areaInHa", "desc");

        // If there are more then 4 items, we are reducing items 4...N into one item called "Other"
        if (newData.length > 4) {
            const otherItem = newData.slice(3).reduce<OverviewChartDataProps>(
                (total, current) => ({
                    ...total,
                    areaInHa: total.areaInHa + current.areaInHa,
                    fieldsNumber: total.fieldsNumber + current.fieldsNumber,
                }),
                {
                    cropName: "Other",
                    areaInHa: 0,
                    cropId: "no-id",
                    fieldsNumber: 0,
                },
            );

            return [...newData.slice(0, 3), otherItem];
        } else {
            return newData;
        }
    }, [overviewData?.crops, t]);

    return (
        <Stack direction="row" alignItems="stretch" gap="3rem" justifyContent="space-between" position="relative">
            {isLoading && (
                <Stack alignItems="center" width="full" paddingY="2.5rem" sx={{width: "100%"}}>
                    <CircularProgress />
                </Stack>
            )}
            {!isLoading && overviewData && (
                <>
                    <Stack direction="column" gap="1.5rem" justifyContent="center">
                        <InsightsOverviewAlert type="moisture" alertsCount={overviewData.moistureAlertsNumber} />
                        <InsightsOverviewAlert type="growth" alertsCount={overviewData.cropAlertsNumber} />
                    </Stack>
                    <InsightsOverviewChart data={data} />
                    <Stack direction="column" justifyContent="top" paddingTop="1rem">
                        <InsightsOverviewLegend data={data} />
                    </Stack>
                </>
            )}
            <Box sx={{position: "absolute", right: "-1.5rem", top: "-1.5rem"}}>
                <HelpPopover i18nKey="help.popover.fields.insight_overview" placement="top" direction="left" />
            </Box>
        </Stack>
    );
};

interface InsightsOverviewAlertsProps {
    type: "moisture" | "growth";
    alertsCount: number;
}

const InsightsOverviewAlert: React.FC<InsightsOverviewAlertsProps> = ({type, alertsCount}) => {
    const {t} = useTranslation();
    return (
        <Stack direction="row" alignItems="center" gap="1.2rem">
            <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="30" cy="30" r="30" fill={type === "moisture" ? "#F67A6D" : "#FFEDA0"} />
                {type === "moisture" ? (
                    <path
                        d="M11.995 2L17.655 7.85961C18.7743 9.01767 19.5368 10.4934 19.8459 12.1001C20.155 13.7068 19.9969 15.3724 19.3915 16.8861C18.7862 18.3997 17.7608 19.6935 16.4451 20.6038C15.1294 21.5141 13.5825 22 12 22C10.4175 22 8.8706 21.5141 7.5549 20.6038C6.23919 19.6935 5.21381 18.3997 4.60847 16.8861C4.00312 15.3724 3.845 13.7068 4.15411 12.1001C4.46321 10.4934 5.22566 9.01767 6.345 7.85961L11.995 2Z"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        transform="translate(18,18)"
                    />
                ) : (
                    <path
                        d="M3.99846 18.002C13.6572 23.7972 21.3841 16.0702 20.4183 1.58218C5.93049 0.616291 -1.79615 8.3435 3.99846 18.002ZM3.99846 18.002C3.99835 18.0018 3.99857 18.0022 3.99846 18.002ZM3.99846 18.002L1.5 20.4995M3.99846 18.002L9.22698 12.7726"
                        stroke="#895A00"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        transform="translate(19,19)"
                    />
                )}
            </svg>
            <Stack direction="row" alignItems="center" gap="0.75rem">
                {alertsCount > 0 && (
                    <Typography
                        fontSize={30}
                        fontWeight={500}
                        color="#404DB8"
                        textTransform="uppercase"
                        marginBottom={0.5}
                        sx={{
                            textUnderlineOffset: 5,
                            textDecoration: "underline",
                            textDecorationColor: "#8B9ADA",
                        }}
                    >
                        {alertsCount}
                    </Typography>
                )}
                <Typography fontSize={16} fontWeight={500} color="#737990" textTransform="uppercase">
                    {alertsCount == 0 && "No"}{" "}
                    {type === "moisture"
                        ? t("insights.overview.alerts-moisture")
                        : type === "growth"
                          ? t("insights.overview.alerts-growth")
                          : t("insights.overview.alerts")}
                </Typography>
            </Stack>
        </Stack>
    );
};

const InsightsOverviewChart: React.FC<{data: OverviewChartDataProps[]}> = ({data}) => {
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const totalAreaInHa = msUtils.getArea(_.sumBy(data, "areaInHa"));
    const totalFields = _.sumBy(data, "fieldsNumber");

    return (
        <Box width={200} height={200}>
            <ResponsiveContainer>
                <PieChart>
                    <Pie
                        data={data}
                        nameKey="cropName"
                        dataKey="areaInHa"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={70}
                        isAnimationActive={false}
                    >
                        {data.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fontSize={30}>
                        <tspan fontSize={30} x="50%" dy="-0.4em" fill="#545A6E">
                            {Number(totalAreaInHa).toFixed(0)}
                        </tspan>
                        <tspan fontSize={14} dx="5" dy="0.2em" fill="#8F94A8">
                            {msUtils.getAreaUnits()}
                        </tspan>
                        <tspan fontSize={16} x="50%" dy="2em" fill="#737990" textDecoration="uppercase">
                            {totalFields} {t("insights.overview.fields")}
                        </tspan>
                    </text>
                </PieChart>
            </ResponsiveContainer>
        </Box>
    );
};

const InsightsOverviewLegend: React.FC<{data: OverviewChartDataProps[]}> = ({data}) => {
    const {t} = useTranslation();
    const msUtils = useMeasureSystemUtils();
    const totalAreaInHa = _.sumBy(data, "areaInHa");

    return (
        <Box>
            <Table
                size="small"
                sx={{
                    minWidth: 300,
                }}
            >
                <TableHead
                    sx={{
                        [`& .${tableCellClasses.root}`]: {
                            color: "#737990",
                            textTransform: "uppercase",
                            fontSize: 16,
                            paddingBottom: 0,
                        },
                    }}
                >
                    <TableRow>
                        <TableCell sx={{paddingLeft: 0}}>{t("insights.field_insights_table.crop")}</TableCell>
                        <TableCell align="right">{t("insights.field_insights_table.area")}</TableCell>
                        <TableCell align="right" sx={{paddingRight: 0}}>
                            %
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody
                    sx={{
                        [`& .${tableCellClasses.root}`]: {
                            borderBottom: "none",
                            color: "#545A6E",
                            fontSize: 14,
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                        },
                    }}
                >
                    {data.map((item, index) => (
                        <TableRow key={item.cropName}>
                            <TableCell sx={{paddingLeft: 0, maxWidth: 140}}>
                                <Stack direction="row" gap="0.75rem" alignItems="center">
                                    <SquareRoundedIcon sx={{color: COLORS[index % COLORS.length]}} />
                                    {item.cropName}
                                </Stack>
                            </TableCell>
                            <TableCell align="right">
                                {Number(msUtils.getArea(item.areaInHa)).toFixed(1)} {msUtils.getAreaUnits()}
                            </TableCell>
                            <TableCell align="right" sx={{paddingRight: 0}}>
                                {Number((item.areaInHa / totalAreaInHa) * 100).toFixed(0)}%
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

interface OverviewChartDataProps {
    cropId: string;
    cropName: string;
    areaInHa: number;
    fieldsNumber: number;
}

const COLORS = ["#8B9ADA", "#7FCDBB", "#FEB24C", "#DBDEE6"];
